import {AfterContentInit, ChangeDetectionStrategy, Component, ElementRef, inject, Input, ViewChild} from '@angular/core';
import {ItemRequest, ItemRequestComment, ItemRequestCommentByDate} from '@store/item-requests/item-requests.types';
import {ReactiveFormsModule, Validators} from '@angular/forms';
import {createSelector, Store} from '@ngrx/store';
import {isPopulatedString} from '@aviary/store/common/typing.helpers';
import {MxSelectorCreator} from '@aviary/store/store.types';
import {sortedCommentsByDate} from '@store/item-requests/item-requests.helpers';
import {ExtendedFormGroup} from '@aviary/forms/extensions/extended-form-group/extended-form-group';
import {ExtendedFormBuilder} from '@aviary/forms/extensions/extended-form-builder/extended-form-builder';
import {getExtendedControlFrom} from '@aviary/forms/form.helpers';
import {ExtendedFormControl} from '@aviary/forms/extensions/extended-form-control/extended-form-control';
import {SelectPipe} from '@aviary/pipes/select.pipe';
import {ItemRequestsSelectors} from '@store/item-requests/item-requests.selectors';
import {PatronSelectors} from '@store/patron/patron.selectors';
import {ItemRequestsActions} from '@store/item-requests/item-requests.actions';
import {DirectMessageComponent} from '@aviary/components/direct-message/direct-message.component';
import {MatTooltip} from '@angular/material/tooltip';
import {IconButtonComponent} from '@aviary/buttons/icon-button/icon-button.component';
import {NgClass} from '@angular/common';
import {FormatPipe} from '@aviary/pipes/format.pipe';
import {Actions, ofType} from '@ngrx/effects';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TextareaFormFieldComponent} from '@aviary/forms/fields/textarea-form-field/textarea-form-field.component';

@Component({
  selector: 'px-item-request-details-messages',
  host: {
    class: 'hk-delegate-vertical-scrolling pe-0'
  },
  templateUrl: './item-request-details-messages.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SelectPipe, ReactiveFormsModule, DirectMessageComponent, MatTooltip, IconButtonComponent, NgClass, FormatPipe, TextareaFormFieldComponent
  ]
})
export class ItemRequestDetailsMessagesComponent implements AfterContentInit {
  @ViewChild('chatMessageList', {read: ElementRef})
  public set chatMessageList(elementRef: ElementRef) {
    this.messagesElement = elementRef?.nativeElement;
    requestAnimationFrame(() => {
      this.scrollToBottom()
    });
  }

  @Input() itemRequest: ItemRequest;

  public readonly selectors = ItemRequestsSelectors.Stateless;
  public readonly patronSelectors = PatronSelectors.Auth;
  public readonly chatFormGroup: ExtendedFormGroup = inject(ExtendedFormBuilder).nonNullable.extendedGroup({
    message: ['', [Validators.maxLength(4000)]],
  });
  public messageControl: ExtendedFormControl = getExtendedControlFrom(this.chatFormGroup, 'message');
  private messagesElement: HTMLElement;
  private readonly actions: Actions = inject(Actions);
  private readonly store: Store = inject(Store);

  constructor() {
    this.actions.pipe(
      ofType(ItemRequestsActions.sendItemRequestMessageSuccess),
      takeUntilDestroyed()
    ).subscribe(() => {
      this.messageControl.reset();
      this.scrollToBottom();
    });
  }

  ngAfterContentInit() {
    this.store.dispatch(ItemRequestsActions.markItemRequestMessagesAsRead());
  }

  public readonly sortedCommentsByDateFor: MxSelectorCreator<[ItemRequestComment[]], ItemRequestCommentByDate[]> =
    comments => createSelector(() => sortedCommentsByDate(comments));

  public onMessageSubmit(): void {
    const message = this.messageControl.value;
    if (!isPopulatedString(message)) {
      return;
    }
    this.store.dispatch(ItemRequestsActions.sendItemRequestMessage(this.itemRequest.id, message));
  }

  public scrollToBottom(): void {
    if (this.messagesElement) {
      this.messagesElement.scrollTo({top: this.messagesElement.scrollHeight, behavior: 'smooth'});
    }
  }

  public readonly messagesEnabledFor: MxSelectorCreator<[ItemRequest], boolean> =
    (itemRequest) => createSelector(
      this.selectors.statusFor(itemRequest),
      status => !['APPROVED', 'DECLINED', 'CANCELED'].includes(status)
    );

  public get formSubmittable(): boolean {
    return this.chatFormGroup.valid && isPopulatedString(this.messageControl.value);
  }
}
